import { memo, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import type { AgentMessage } from '@multiplayer-app/ai-agent-types'
import { useVirtualizer } from '@tanstack/react-virtual'

import { useAgentStore } from '@/context/AgentProvider'
import { useMessagesInfinite } from '@/hooks/useMessagesInfinite'
import { useTranslations } from '@/hooks/useTranslations'
import { UiChatStatus, getChatState, type AgentStore } from '@/state/useAgentStore'
import ComposerError from './ComposerError'
import MessageGroup from './MessageGroup'

const EMPTY_MESSAGES: AgentMessage[] = []
const ESTIMATED_MESSAGE_HEIGHT = 120

const getMessagesForActiveChat = (state: AgentStore): AgentMessage[] => {
  const activeChatId = state.activeChatId
  if (!activeChatId) return EMPTY_MESSAGES
  return state.chatState[activeChatId]?.messages ?? EMPTY_MESSAGES
}

interface MessageListProps {
  showParticipantChrome?: boolean
}

const MessageList = ({ showParticipantChrome = false }: MessageListProps) => {
  const t = useTranslations()
  const contextKey = useAgentStore((state) => state.contextKey)
  const draftChatKey = useAgentStore((state) => state.draftChatKey)
  const activeChatId = useAgentStore((state) => state.activeChatId)
  const messages = useAgentStore(getMessagesForActiveChat)
  const pendingKey = activeChatId ?? draftChatKey

  const messagesQuery = useMessagesInfinite(activeChatId)

  const pendingContentLength = useAgentStore(
    (state) => getChatState(state, pendingKey).pendingAssistant?.content?.length
  )
  const pendingId = useAgentStore((state) => getChatState(state, pendingKey).pendingAssistant?.id)
  const activeChatStatus = useAgentStore((state) => getChatState(state, pendingKey).status)
  const isStreaming = activeChatStatus === UiChatStatus.Sending || activeChatStatus === UiChatStatus.Streaming

  const containerRef = useRef<HTMLDivElement>(null)
  const stickToBottomRef = useRef(true)
  const hasInitialScrollRef = useRef(false)
  const suppressScrollHandlerRef = useRef(false)
  const prependAnchorRef = useRef<{
    scrollHeight: number
    scrollTop: number
    count: number
    wasAtBottom: boolean
  } | null>(null)

  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => ESTIMATED_MESSAGE_HEIGHT,
    overscan: 8,
    gap: 16,
    getItemKey: (index) => messages[index]?.id ?? index
  })

  const virtualItems = virtualizer.getVirtualItems()

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [])

  // Initial scroll + scroll on new messages appended at bottom
  useEffect(() => {
    if (messages.length === 0) return

    if (!hasInitialScrollRef.current) {
      hasInitialScrollRef.current = true
      suppressScrollHandlerRef.current = true
      virtualizer.scrollToIndex(messages.length - 1, { align: 'end' })
      requestAnimationFrame(() => {
        scrollToBottom()
        requestAnimationFrame(() => {
          scrollToBottom()
          suppressScrollHandlerRef.current = false
        })
      })
      return
    }

    if (stickToBottomRef.current) {
      requestAnimationFrame(scrollToBottom)
    }
  }, [messages.length, virtualizer, scrollToBottom])

  // Restore scroll position after older pages are prepended at the top
  useLayoutEffect(() => {
    const anchor = prependAnchorRef.current
    const el = containerRef.current
    if (!anchor || !el) return
    if (messagesQuery.isFetchingNextPage) return
    if (messages.length <= anchor.count) return

    suppressScrollHandlerRef.current = true

    if (anchor.wasAtBottom) {
      el.scrollTop = el.scrollHeight
    } else {
      el.scrollTop = anchor.scrollTop + (el.scrollHeight - anchor.scrollHeight)
    }

    prependAnchorRef.current = null
    requestAnimationFrame(() => {
      suppressScrollHandlerRef.current = false
    })
  }, [messages.length, messagesQuery.isFetchingNextPage])

  // Keep scroll pinned to bottom while streaming
  useEffect(() => {
    if (isStreaming && stickToBottomRef.current) {
      scrollToBottom()
    }
  }, [pendingContentLength, isStreaming, scrollToBottom])

  // Track whether user has scrolled away from the bottom
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const handleScroll = () => {
      if (suppressScrollHandlerRef.current) return
      const { scrollTop, scrollHeight, clientHeight } = el
      stickToBottomRef.current = scrollHeight - scrollTop - clientHeight < 150
    }

    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [])

  // Trigger load-more when the first rendered virtual item is near the top
  const firstVirtualIndex = virtualItems[0]?.index ?? Number.POSITIVE_INFINITY
  useEffect(() => {
    if (!messagesQuery.hasNextPage || messagesQuery.isFetchingNextPage || firstVirtualIndex > 1) return

    const el = containerRef.current
    if (el) {
      prependAnchorRef.current = {
        scrollHeight: el.scrollHeight,
        scrollTop: el.scrollTop,
        count: messages.length,
        wasAtBottom: stickToBottomRef.current
      }
    }
    void messagesQuery.fetchNextPage()
  }, [
    firstVirtualIndex,
    messagesQuery.hasNextPage,
    messagesQuery.isFetchingNextPage,
    messagesQuery.fetchNextPage,
    messages.length
  ])

  if (!activeChatId && messages.length === 0 && !pendingId) {
    return (
      <div className='mp-agent-messages-empty'>
        <div className='mp-agent-messages-empty-content'>
          <h4>{t.helloThere}</h4>
          <p>{t.howCanIHelpYou}</p>
        </div>
      </div>
    )
  }

  if (activeChatId && messages.length === 0 && messagesQuery.isLoading) {
    return (
      <div className='mp-agent-messages-empty'>
        <div className='mp-agent-messages-empty-content'>
          <p>{t.fetchingChats}</p>
        </div>
      </div>
    )
  }

  return (
    <div className='mp-agent-messages' ref={containerRef}>
      {messagesQuery.isFetchingNextPage && (
        <div className='mp-agent-messages-load-more'>
          <div className='mp-agent-messages-load-more-text'>{t.fetchingChats}</div>
        </div>
      )}
      <div className='mp-agent-messages-virtual-list' style={{ height: virtualizer.getTotalSize() }}>
        {virtualItems.map((virtualItem) => {
          const message = messages[virtualItem.index]
          if (!message) return null
          return (
            <div
              key={virtualItem.key}
              ref={virtualizer.measureElement}
              data-index={virtualItem.index}
              className='mp-agent-messages-virtual-item'
              style={{ transform: `translateY(${virtualItem.start}px)` }}
            >
              <MessageGroup message={message} contextKey={contextKey} showParticipantChrome={showParticipantChrome} />
            </div>
          )
        })}
      </div>
      {!!pendingId && (
        <PendingAssistantMessage
          pendingKey={pendingKey}
          contextKey={contextKey}
          showParticipantChrome={showParticipantChrome}
        />
      )}
      <ComposerError />
    </div>
  )
}

const PendingAssistantMessage = memo(
  ({
    pendingKey,
    contextKey,
    showParticipantChrome
  }: {
    pendingKey: string
    contextKey: string
    showParticipantChrome: boolean
  }) => {
    const cs = useAgentStore((state) => getChatState(state, pendingKey))
    const pendingAssistant = cs.pendingAssistant
    const activeChatStatus = cs.status

    if (!pendingAssistant) return null

    const isStreaming = activeChatStatus === UiChatStatus.Sending || activeChatStatus === UiChatStatus.Streaming

    return (
      <MessageGroup
        key={pendingAssistant.id}
        message={pendingAssistant}
        contextKey={contextKey}
        isStreaming={isStreaming}
        showParticipantChrome={showParticipantChrome}
      />
    )
  }
)

export default MessageList
